import { Activity } from '@models/activity.model';
import { ActivityHeader } from '../ActivityHeader';
import { TaskName } from '../TaskName';
import { formatActivityTime } from '@utils/formatDate';
import WarningAmberOutlinedIcon from '@mui/icons-material/WarningAmberOutlined';
import EmojiObjectsOutlinedIcon from '@mui/icons-material/EmojiObjectsOutlined';
import ReportProblemOutlinedIcon from '@mui/icons-material/ReportProblemOutlined';
import { Card } from '@components/atoms/Card';
import Alert from '@components/atoms/Alert';
import { useTranslation } from '@hooks/useTranslation';

export const ActivityDelegationCanceled = ({
  activity,
  handleOpenTaskDetail,
}: {
  activity: Activity;
  handleOpenTaskDetail?: (taskId: string) => void;
}) => {
  const tActivityCards = useTranslation('activityCards');
  const projectName = activity.project?.name || tActivityCards.notAvailable;

  const rightLabel = (
    <div className="flex items-center gap-1">
      <WarningAmberOutlinedIcon fontSize="small" className="text-orange-800" />
      <p className="text-sm text-orange-800">{tActivityCards.delegationCanceled.taskDelegationCancelled}</p>
      {activity.createdAt && (
        <p className="text-sm text-neutral-500">
          {`- ${formatActivityTime(activity.createdAt)}`}
        </p>
      )}
    </div>
  );

  return (
    <Card>
      <ActivityHeader activity={activity} rightLabel={rightLabel} />

      <div className="mt-4 space-y-3">
        {/* Context Information Section */}
        <Alert>
          <div className="flex flex-col gap-1">
            <TaskName
              task={activity.task}
              size="sm"
              taskId={activity.taskId || activity.task?.taskId}
              onOpenTask={handleOpenTaskDetail}
            />
            <div className="flex gap-1">
              <p className="text-sm text-neutral-800 font-semibold">{tActivityCards.projectLabel}</p>
              <p className="text-sm text-neutral-800">{projectName}</p>
            </div>
          </div>
        </Alert>

        {/* Delegation Cancelled Panel */}
        <div className="bg-orange-50 rounded-md p-3 ">
          <div className="text-sm space-y-2">
            <div className="flex items-center justify-between">
              <span className="flex gap-1 items-center">
                <ReportProblemOutlinedIcon
                  fontSize="small"
                  className="text-orange-800"
                />
                <p className="text-orange-800 text-sm font-medium">
                  {tActivityCards.delegationCanceled.delegationCancelledPanel}
                </p>
              </span>
            </div>

            <div className="bg-white bg-opacity-60 rounded-md p-2 border border-orange-200">
              <div className="flex items-center gap-1">
                <EmojiObjectsOutlinedIcon
                  fontSize="small"
                  className="text-orange-800"
                />
                <p className="text-orange-800 text-xs font-medium">
                  {tActivityCards.delegationCanceled.availableForReassignmentHint}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Description Section */}
        <Alert className="bg-gray-50 rounded-md p-3">
          <p className="text-gray-800 text-sm leading-relaxed">
            {tActivityCards.delegationCanceled.description}
          </p>
        </Alert>
      </div>
    </Card>
  );
};
